import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    CheckCircleRounded,
    ErrorOutlineRounded,
    TaskAltRounded,
} from "@mui/icons-material";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import {
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_READINESS_CHECK_SX,
    ACCOUNT_PAGE_READINESS_CHECKS_SX,
    ACCOUNT_PAGE_READINESS_COUNT_SX,
    ACCOUNT_PAGE_READINESS_HEADER_ICON_SX,
    ACCOUNT_PAGE_READINESS_HEADER_SX,
    ACCOUNT_PAGE_READINESS_INTRO_SX,
    ACCOUNT_PAGE_READINESS_KICKER_SX,
    ACCOUNT_PAGE_READINESS_PROGRESS_SEGMENT_SX,
    ACCOUNT_PAGE_READINESS_PROGRESS_TRACK_SX,
    ACCOUNT_PAGE_READINESS_SX,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();

function AccountProfileReadiness({
    user,
    refreshToken = null,
    onReadinessChange,
}) {
    const [readiness, setReadiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const accountStatus = user?.accountStatus;

    useEffect(() => {
        if (!user?.userId || accountStatus === "verified") {
            setReadiness(null);
            setLoading(false);
            onReadinessChange?.(false);
            return undefined;
        }

        let cancelled = false;

        setLoading(true);
        setReadiness(null);
        onReadinessChange?.(false);

        async function fetchReadiness() {
            try {
                const res = await fetch(`${basePath}/api/v1/account/readiness`, {
                    credentials: "include",
                });
                const data = await res.json().catch(() => null);
                if (!cancelled && res.ok && data?.ok && data.readiness) {
                    setReadiness(data.readiness);
                    onReadinessChange?.(Boolean(data.readiness.allDone));
                } else if (!cancelled) {
                    setReadiness(null);
                    onReadinessChange?.(false);
                }
            } catch (_) {
                if (!cancelled) {
                    setReadiness(null);
                    onReadinessChange?.(false);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchReadiness();

        return () => {
            cancelled = true;
        };
    }, [user?.userId, accountStatus, refreshToken, onReadinessChange]);

    const checkEntries = useMemo(
        () => Object.entries(readiness?.checks || {}),
        [readiness?.checks],
    );

    if (loading || !readiness || readiness.allDone || accountStatus === "verified") {
        return null;
    }

    const { completed, total } = readiness;
    const segments = Math.max(total, 1);
    const filledSegs =
        total > 0 ? Math.round((completed / total) * segments) : 0;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
    const remaining = Math.max(total - completed, 0);

    return (
        <Box component="section" sx={ACCOUNT_PAGE_READINESS_SX} aria-labelledby="profile-readiness-title">
            <Box sx={ACCOUNT_PAGE_READINESS_HEADER_SX}>
                <Box sx={ACCOUNT_PAGE_READINESS_HEADER_ICON_SX} aria-hidden="true">
                    <TaskAltRounded />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={ACCOUNT_PAGE_READINESS_KICKER_SX}>Account setup</Typography>
                    <Typography id="profile-readiness-title" component="h2" sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>
                        Profile readiness
                    </Typography>
                    <Typography sx={ACCOUNT_PAGE_READINESS_INTRO_SX}>
                        {remaining === 1
                            ? "One detail left to complete your profile."
                            : `${remaining} details left to complete your profile.`}
                    </Typography>
                </Box>
                <Typography sx={ACCOUNT_PAGE_READINESS_COUNT_SX}>
                    {progressPercent}
                    <Box
                        component="span"
                        sx={{ color: colors.text.secondary, fontWeight: 600, fontSize: "0.72rem" }}
                    >
                        %
                    </Box>
                </Typography>
            </Box>

            <Box
                sx={ACCOUNT_PAGE_READINESS_PROGRESS_TRACK_SX}
                role="progressbar"
                aria-label="Profile readiness"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={completed}
                aria-valuetext={`${completed} of ${total} profile requirements complete`}
            >
                {Array.from({ length: segments }).map((_, index) => (
                    <Box
                        key={`readiness-seg-${index}`}
                        sx={{
                            ...ACCOUNT_PAGE_READINESS_PROGRESS_SEGMENT_SX,
                            background:
                                index < filledSegs
                                    ? `linear-gradient(90deg, ${alpha(colors.primary.main, 0.55)}, ${colors.primary.main})`
                                    : "rgba(255,255,255,0.08)",
                        }}
                    />
                ))}
            </Box>

            <Box sx={ACCOUNT_PAGE_READINESS_CHECKS_SX}>
                {checkEntries.map(([key, check]) => {
                    const isDone = Boolean(check?.done);
                    const statusText = isDone ? "Complete" : "Action needed";

                    return (
                        <Box
                            key={key}
                            sx={{
                                ...ACCOUNT_PAGE_READINESS_CHECK_SX,
                                borderColor: alpha(
                                    isDone ? colors.state.success.main : colors.primary.main,
                                    0.34,
                                ),
                                background: alpha(
                                    isDone ? colors.state.success.main : colors.primary.main,
                                    0.09,
                                ),
                            }}
                        >
                            {isDone ? (
                                <CheckCircleRounded sx={{ fontSize: "1.1rem", color: colors.state.success.main }} />
                            ) : (
                                <ErrorOutlineRounded sx={{ fontSize: "1.1rem", color: colors.primary.main }} />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography component="span" sx={{ display: "block", color: colors.text.primary, fontSize: "0.8rem", fontWeight: 650, lineHeight: 1.25 }}>
                                    {check?.label}
                                </Typography>
                                <Typography component="span" sx={{ display: "block", mt: "0.16rem", color: colors.text.secondary, fontSize: "0.71rem", fontWeight: 500, lineHeight: 1.35 }}>
                                    {isDone ? statusText : check?.warningLabel || statusText}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

AccountProfileReadiness.propTypes = {
    user: PropTypes.object,
    refreshToken: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    onReadinessChange: PropTypes.func,
};

export default React.memo(AccountProfileReadiness);
